import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiCallError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError } from '@/components/ui/field-error';

export interface CompanyProfile {
  companyName: string | null;
  companyAddress: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  companyWebsite: string | null;
  companySocials: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: CompanyProfile;
}

/**
 * Dialog del panel del tenant para editar los DATOS DE LA EMPRESA que se usan
 * como encabezado de los presupuestos (en lugar de la marca Actuo).
 */
export function AppCompanyEditDialog({ open, onOpenChange, initial }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CompanyProfile>(initial);
  const [issues, setIssues] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (open) {
      setForm(initial);
      setIssues({});
    }
  }, [open, initial]);

  const mutation = useMutation({
    mutationFn: () => {
      const norm = (v: string | null) => (v && v.trim() ? v.trim() : null);
      return api('/api/app/me', {
        method: 'PATCH',
        body: JSON.stringify({
          companyName: norm(form.companyName),
          companyAddress: norm(form.companyAddress),
          companyPhone: norm(form.companyPhone),
          companyEmail: norm(form.companyEmail),
          companyWebsite: norm(form.companyWebsite),
          companySocials: norm(form.companySocials),
        }),
      });
    },
    onSuccess: () => {
      toast.success('Datos de la empresa actualizados');
      qc.invalidateQueries({ queryKey: ['app-me'] });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiCallError && err.status === 400) {
        const body = err.body as { issues?: Record<string, string[]> };
        if (body.issues) setIssues(body.issues);
      } else {
        toast.error('No se pudieron guardar los cambios');
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setIssues({});
    mutation.mutate();
  }

  const set = (k: keyof CompanyProfile) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Datos de la empresa</DialogTitle>
            <DialogDescription>
              Se usan en tus documentos comerciales. Si dejás el nombre vacío, usamos tu nombre de
              cuenta.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="companyName">Nombre de la empresa</Label>
              <Input
                id="companyName"
                value={form.companyName ?? ''}
                onChange={set('companyName')}
                placeholder="Mi Empresa S.A."
                autoFocus
              />
              <FieldError error={issues.companyName} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="companyAddress">Dirección</Label>
              <Input
                id="companyAddress"
                value={form.companyAddress ?? ''}
                onChange={set('companyAddress')}
                placeholder="Av. Mariscal López 123, Asunción"
              />
              <FieldError error={issues.companyAddress} />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="companyPhone">Teléfono</Label>
                <Input
                  id="companyPhone"
                  value={form.companyPhone ?? ''}
                  onChange={set('companyPhone')}
                  placeholder="0991 123 456"
                />
                <FieldError error={issues.companyPhone} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="companyEmail">Email</Label>
                <Input
                  id="companyEmail"
                  type="email"
                  value={form.companyEmail ?? ''}
                  onChange={set('companyEmail')}
                  placeholder="contacto@empresa.com"
                />
                <FieldError error={issues.companyEmail} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="companyWebsite">Web</Label>
              <Input
                id="companyWebsite"
                value={form.companyWebsite ?? ''}
                onChange={set('companyWebsite')}
                placeholder="www.empresa.com"
              />
              <FieldError error={issues.companyWebsite} />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="companySocials">Redes</Label>
              <Input
                id="companySocials"
                value={form.companySocials ?? ''}
                onChange={set('companySocials')}
                placeholder="@miempresa (IG / FB)"
              />
              <FieldError error={issues.companySocials} />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
